import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';
import React, { useEffect, useState } from 'react';
import './Escrow.less';
import { Modal, Tabs } from 'antd';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import { DollarOutlined } from '@ant-design/icons';
import AdvancedModalFilters from '@portal/components/AdvancedModalFilters/AdvancedModalFilters';
import { AdvancedFilterType } from '@portal/models/enumerators';
import { useDispatch } from 'react-redux';
import { ProductsActions, SettingsActions } from '@portal/actions';
import { TermsTypes } from '@portal/models/enumerators/onboarding';
import useReduxState from '@portal/hooks/useReduxState';
import { getSettingsRemote } from '@portal/actions/onboarding';
import { queryStringToObject } from '@portal/services/queryString';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import { formatCurrency } from '@portal/services/masks';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { useNavigation } from '@portal/hooks/useNavigation';

const initialFilters: advancedFilterModels.baseFilter = {
  orderBy: 'createdAt',
  page: 0,
  pageSize: 10,
  sort: 'desc',
};

enum TabIndexEnum {
  USED = 1,
  RETURNED = 2,
}

const Escrow = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const {
    onboarding: { terms },
    settings: { keys },
    products: {
      escrowAccount,
      alreadyCreatedEscrowAccount,
      escrowTransactions,
    },
  } = useReduxState();

  const [tabKey, setTabKey] = useState<TabIndexEnum>(TabIndexEnum.USED);
  const filterParams = new URLSearchParams(location.search);
  const [modalTerms, setModalTerms] = useState({
    visible: false,
    type: TermsTypes.TERMS_OF_USE,
  });
  const [advancedFilters, setAdvancedFilters] = useState(initialFilters);
  const [modalAdherenceVisible, setModalAdherenceVisible] = useState(false);

  useEffect(() => {
    if (!alreadyCreatedEscrowAccount) {
      setModalAdherenceVisible(false);
    }
  }, []);

  useEffect(() => {
    if (terms.length === 0) {
      dispatch(getSettingsRemote());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      SettingsActions.getSettingsKeys([
        TermsTypes.PF_TARIFF_TABLE,
        TermsTypes.PJ_TARIFF_TABLE,
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      ProductsActions.getEscrowTransactions({
        ...advancedFilters,
      })
    );
  }, [dispatch, advancedFilters]);

  const getTermByType = (type: string) => {
    if (
      type === TermsTypes.PJ_TARIFF_TABLE ||
      type === TermsTypes.PF_TARIFF_TABLE
    ) {
      const key = keys?.find((key) => key[type]);
      return key ? key[type] : ({ token: '', type, url: '' } as models.Terms);
    } else {
      const term = terms.find((term) => term.type === type);
      return term ?? ({ token: '', type, url: '' } as models.Terms);
    }
  };

  const renderLimits = () => {
    const limits = [
      {
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.TOTAL_LIMIT'),
        value: formatCurrency(escrowAccount?.limit ?? 0),
      },
      {
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.AVAILABLE_LIMIT'),
        value: formatCurrency(escrowAccount?.available ?? 0),
      },
      {
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.LIMIT_USED'),
        value: formatCurrency(escrowAccount?.used ?? 0),
      },
      {
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.INTEREST'),
        value: escrowAccount?.interest ?? '0%',
      },
    ];

    return (
      <div className="escrow-account__card">
        {limits.map((limit) => (
          <div key={limit.title} className="escrow-account__card__item">
            <div>
              <p>{limit.title}</p>
              <div>
                <DollarOutlined />
              </div>
            </div>
            <h3>{limit.value}</h3>
          </div>
        ))}
      </div>
    );
  };

  const renderExtract = (tab: TabIndexEnum) => {
    const transactions =
      escrowTransactions?.filter(
        (transaction) =>
          transaction.type ===
          (tab === TabIndexEnum.USED ? 'negative' : 'positive')
      ) || [];

    return (
      <div>
        <div className="escrow-account__summary">
          <div className="escrow-account__summary__item">
            <div className="escrow-account__summary__item__label">
              {translate('PAGES.PRODUCTS.ESCROW.EXTRACT.TOTAL_AMOUNT')}
            </div>
            <div
              className={`escrow-account__summary__item__value ${
                tab === TabIndexEnum.USED ? 'negative' : 'positive'
              }`}
            >
              {tab === TabIndexEnum.USED
                ? formatCurrency(0)
                : formatCurrency(0)}
            </div>
          </div>
          <div className="escrow-account__summary__item">
            <div className="escrow-account__summary__item__label">
              {translate('PAGES.PRODUCTS.ESCROW.EXTRACT.OPERATION')}
            </div>
            <div className="escrow-account__summary__item__value">
              {transactions.length}
            </div>
          </div>
          <div className="escrow-account__summary__item">
            <div className="escrow-account__summary__item__label">
              {translate('PAGES.PRODUCTS.ESCROW.EXTRACT.AVERAGE_PER_OPERATION')}
            </div>
            <div className="escrow-account__summary__item__value">
              {tab === TabIndexEnum.USED
                ? formatCurrency(0)
                : formatCurrency(0)}
            </div>
          </div>
        </div>
        <div>
          {transactions.map((transaction) => (
            <div key={transaction.id} className="escrow-account__section">
              <div className="escrow-account__section__info">
                <p>{transaction.title}</p>
                <span>{transaction.date}</span>
              </div>
              <div
                className={`escrow-account__section__value ${
                  transaction.type === 'negative' ? 'negative' : 'positive'
                }`}
              >
                <p>{transaction.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const tabOptions = [
    {
      label: translate('PAGES.PRODUCTS.ESCROW.TABS.USED'),
      value: TabIndexEnum.USED,
    },
    {
      label: translate('PAGES.PRODUCTS.ESCROW.TABS.RETURNED'),
      value: TabIndexEnum.RETURNED,
    },
  ];

  const renderActions = () => {
    const actions = [
      {
        label: translate('PAGES.PRODUCTS.ESCROW.BUTTONS.LIMITS'),
        action: () =>
          navigate(getRouteStackPath('PRODUCTS', 'LIMITS_ESCROW_OPERATIONAL')),
        disable: false,
      },
      {
        label: translate('PAGES.PRODUCTS.ESCROW.BUTTONS.TERMS'),
        action: () =>
          setModalTerms({ visible: true, type: TermsTypes.TERMS_OF_USE }),
        disable: false,
      },
      {
        label: translate('PAGES.PRODUCTS.ESCROW.BUTTONS.EXPORT'),
        disable: true,
      },
      {
        label: translate('PAGES.PRODUCTS.ESCROW.BUTTONS.CLOSE_ACCOUNT'),
        disable: true,
      },
    ];

    return (
      <div className="escrow-account__actions">
        {actions.map((action) => (
          <button
            key={action.label}
            onClick={action.action}
            disabled={action.disable}
            className={`escrow-account__actions__button ${
              action.disable ? 'disabled' : ''
            }`}
          >
            {action.label}
          </button>
        ))}
      </div>
    );
  };

  const onSearch = (filters: advancedFilterModels.baseFilter) => {
    const queryParams = queryStringToObject();
    const newFilter = {
      ...queryParams,
      ...filters,
    };
    setAdvancedFilters(newFilter);
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.PRODUCTS'),
          },
          {
            active: false,
            title: translate('PAGES.PRODUCTS.ESCROW.TITLE'),
          },
        ]}
      />
      <div className="escrow-account">
        <AdvancedPanelContentHeader
          testId="escrow-account-header"
          title={translate('PAGES.PRODUCTS.ESCROW.TITLE')}
          description={translate('PAGES.PRODUCTS.ESCROW.DESCRIPTION')}
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
        />
        {renderLimits()}
        {renderActions()}
        <AdvancedCard testId="escrow-extract-card">
          <h3>{translate('PAGES.PRODUCTS.ESCROW.EXTRACT.TITLE')}</h3>
          <AdvancedModalFilters
            fields={[
              {
                name: 'dateRange',
                placeholder: [
                  translate(
                    'PAGES.CONDOMINIUMS.ADVANCED_FILTER.CREATED_AT_START'
                  ),
                  translate(
                    'PAGES.CONDOMINIUMS.ADVANCED_FILTER.CREATED_AT_END'
                  ),
                ] as [string, string],
                type: AdvancedFilterType.RANGE_PICKER,
                defaultValue: filterParams.get('dateRange') ?? '',
                disableFuture: true,
                onlyDate: true,
                freeField: true,
              },
            ]}
            onSearch={onSearch}
          />
          <Tabs
            activeKey={String(tabKey)}
            onChange={(key) => setTabKey(Number(key))}
            type="card"
          >
            {tabOptions.map((tab) => (
              <Tabs.TabPane tab={tab.label} key={String(tab.value)}>
                {renderExtract(tab.value)}
              </Tabs.TabPane>
            ))}
          </Tabs>
        </AdvancedCard>
      </div>
      <Modal
        open={modalTerms.visible}
        footer={null}
        title={null}
        closable
        onCancel={() =>
          setModalTerms({
            visible: false,
            type: TermsTypes.TERMS_OF_USE,
          })
        }
        width={1000}
      >
        <p className="terms-of-use__title">
          {translate(
            `PAGES.MY_ACCOUNT.TABS.${
              modalTerms.type === TermsTypes.TERMS_OF_USE
                ? 'TERMS_OF_USE'
                : modalTerms.type === TermsTypes.PF_TARIFF_TABLE ||
                  modalTerms.type === TermsTypes.PJ_TARIFF_TABLE
                ? 'TARIFF_TABLE'
                : 'PRIVACY_POLICY'
            }`
          )}
        </p>
        <iframe
          src={
            modalTerms.type === TermsTypes.PF_TARIFF_TABLE ||
            modalTerms.type === TermsTypes.PJ_TARIFF_TABLE
              ? `${getTermByType(modalTerms.type)}#toolbar=0`
              : `${getTermByType(modalTerms.type).url}#toolbar=0`
          }
          width="100%"
          height={500}
        />
      </Modal>
      <GenericModal
        title={translate('PAGES.PRODUCTS.ESCROW.ADHERENCE_PRODUCT.TITLE')}
        visible={modalAdherenceVisible}
        description={translate(
          'PAGES.PRODUCTS.ESCROW.ADHERENCE_PRODUCT.DESCRIPTION'
        )}
        onClose={() => setModalAdherenceVisible(false)}
        actions={[
          {
            label: translate('PAGES.PRODUCTS.ESCROW.ADHERENCE_PRODUCT.CANCEL'),
            onClick: () => setModalAdherenceVisible(false),
            variant: 'secondary',
          },
          {
            label: translate('PAGES.PRODUCTS.ESCROW.ADHERENCE_PRODUCT.BUTTON'),
            onClick: () => {
              setModalAdherenceVisible(false);
            },
            variant: 'primary',
          },
        ]}
      />
    </>
  );
};

export default Escrow;
